/**
 * Studio Basic Info component
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building, Globe, Instagram, Linkedin } from "lucide-react";
import ImageUpload from "./ImageUpload";

export function StudioBasicInfo({
  studioName, setStudioName,
  studioPhone, setStudioPhone,
  yearsInBusiness, setYearsInBusiness,
  studioLogo, setStudioLogo,
  websiteUrl, setWebsiteUrl,
  instagramHandle, setInstagramHandle,
  facebookUrl, setFacebookUrl,
  linkedinUrl, setLinkedinUrl,
  token
}) {
  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-6">
        <Building className="w-5 h-5 inline mr-2" />
        Studio Information
      </h2>
      
      <div className="space-y-6">
        {/* Studio Logo */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider mb-4 block">Studio Logo</Label>
          <ImageUpload
            currentImage={studioLogo}
            onImageChange={setStudioLogo}
            token={token}
            label="Upload Logo"
            shape="square"
            size="lg"
            fallbackText={studioName ? studioName.charAt(0).toUpperCase() : "S"}
          />
          <p className="text-white/40 text-sm mt-3">
            💡 For best results, use a logo with a transparent or light-colored background. 
            Dark logos may not display well on our dark interface.
          </p>
        </div>

        {/* Studio Name */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Studio Name *</Label>
          <Input
            value={studioName}
            onChange={(e) => setStudioName(e.target.value)}
            placeholder="Your Studio Name"
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
            data-testid="studio-name-input"
          />
        </div>

        {/* Phone Number */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Phone Number</Label>
          <Input
            type="tel"
            value={studioPhone || ""}
            onChange={(e) => setStudioPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
            data-testid="studio-phone-input"
          />
          <p className="text-white/40 text-sm mt-1">Shared with hired candidates only.</p>
        </div>

        {/* Years in Business */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Years in Business</Label>
          <Input
            type="number"
            min="0"
            value={yearsInBusiness}
            onChange={(e) => setYearsInBusiness(e.target.value)}
            placeholder="5"
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm w-32"
            data-testid="years-business-input"
          />
        </div>

        {/* Social Links */}
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-sm text-white/60 uppercase tracking-wider mb-4">
            <Globe className="w-4 h-4 inline mr-2" />
            Online Presence
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-white/60">Website</Label>
              <Input
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://yourstudio.com"
                className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                data-testid="studio-website-input"
              />
            </div>
            <div>
              <Label className="text-sm text-white/60">
                <Instagram className="w-4 h-4 inline mr-1" />
                Instagram Handle
              </Label>
              <Input
                value={instagramHandle}
                onChange={(e) => setInstagramHandle(e.target.value)}
                placeholder="@yourstudio"
                className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                data-testid="studio-instagram-input"
              />
            </div>
            <div>
              <Label className="text-sm text-white/60">Facebook URL</Label>
              <Input
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                placeholder="https://facebook.com/yourstudio"
                className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                data-testid="studio-facebook-input"
              />
            </div>
            <div>
              <Label className="text-sm text-white/60">
                <Linkedin className="w-4 h-4 inline mr-1" />
                LinkedIn URL
              </Label>
              <Input
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/company/yourstudio"
                className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
                data-testid="studio-linkedin-input"
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default StudioBasicInfo;
